/**
 * Ctrl+K / ⌘K (or the "Search" button in the sidebar): jump to any page you can open, or find a SKU by
 * code, name, ASIN or FSN and open it in SKU Master. Pages come from the sidebar's own list, so the
 * palette never offers a page your role cannot use.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from './ui/command';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Plain "contains" matching: loose letter-by-letter matching made "auro" pick "Purchase Orders".
const containsFilter = (value, search) => (value.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0);
const COMMAND_CLASSES = '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5';

export const CommandPalette = ({ open, onOpenChange, groups, country }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [skus, setSkus] = useState([]);

  useEffect(() => { if (!open) { setQuery(''); setSkus([]); } }, [open]);

  // SKU search after a short pause in typing (server-side search, max 8 results)
  useEffect(() => {
    const term = query.trim();
    if (!open || term.length < 2) { setSkus([]); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      axios.get(`${API}/skus/catalogue`, { params: { search: term, page_size: 10, country }, signal: controller.signal })
        .then(({ data }) => setSkus((data.rows || []).slice(0, 8)))
        .catch(() => {});
    }, 200);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, open, country]);

  const go = path => { onOpenChange(false); navigate(path); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-xl" data-testid="command-palette">
        <DialogTitle className="sr-only">Search pages and SKUs</DialogTitle>
        <Command filter={containsFilter} className={COMMAND_CLASSES}>
          <CommandInput placeholder="Go to a page or find a SKU…" value={query} onValueChange={setQuery} data-testid="command-input" />
          <CommandList>
            <CommandEmpty>{query.trim().length >= 2 ? 'No page or SKU matches.' : 'No page matches.'}</CommandEmpty>
            {skus.length > 0 && (
              <CommandGroup heading="SKUs">
                {skus.map(sku => (
                  <CommandItem key={sku.sku_code} value={`sku ${sku.sku_code} ${sku.product_name || ''} ${query}`}
                    onSelect={() => go(`/skus?q=${encodeURIComponent(sku.sku_code)}`)} data-testid={`command-sku-${sku.sku_code}`}>
                    <Package className="mr-2 h-4 w-4 text-gray-500" aria-hidden="true" />
                    <span className="font-mono text-[12px] text-gray-500 mr-2">{sku.sku_code}</span>
                    <span className="truncate">{sku.product_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {groups.map(group => (
              <CommandGroup key={group.id} heading={group.label}>
                {group.items.map(item => {
                  const Icon = item.icon;
                  return (
                    <CommandItem key={item.path} value={`${item.label} ${group.label}`} onSelect={() => go(item.path)} data-testid={`command-page-${item.path}`}>
                      <Icon className="mr-2 h-4 w-4 text-gray-500" aria-hidden="true" />
                      {item.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
          <div className="flex items-center justify-between border-t px-3 py-2 text-[11px] text-gray-500">
            <span>↑ ↓ to move · Enter to open · Esc to close</span>
            <CommandShortcut>Ctrl K</CommandShortcut>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
